import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsersStore } from '../store/useUsersStore';
import { markAllNotificationsRead, getNotificationDetails } from '../utils/api';
import { useNotifications } from '../contexts/NotificationContext';
import { useModal } from '../contexts/ModalContext';
import ProjectDetailsModal from './ProjectDetailsModal';
import { useFirebaseAuth } from '../contexts/FirebaseAuthContext';

const formatTime = (ts) => {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const Notifications = () => {
  const navigate = useNavigate();
 const { items, unreadCount, markRead, markAllRead, remove, pagination, hasMore, loadMore, goToPage, loadPage } = useNotifications();
  const { modalState, openModal, closeModal } = useModal();
  const [pendingIds, setPendingIds] = useState(new Set());
  const [pendingViewIds, setPendingViewIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [markAllReadPending, setMarkAllReadPending] = useState(false);
  const { user: fbUser } = useFirebaseAuth();
  const selectedSubUser = useUsersStore((s) => s.getSelectedUser && s.getSelectedUser());

  const grouped = useMemo(() => items, [items]);

  const handleView = async (notification) => {
    const subUserId = selectedSubUser?.document_id || selectedSubUser?.sub_user_id || selectedSubUser?.id;
    if (!subUserId) {
      openModal('projectDetails', notification.projectData || null);
      return;
    }

    const id = notification.id;
    try {
      setPendingViewIds(prev => new Set(prev).add(id));
      const idToken = typeof fbUser?.getIdToken === 'function' ? await fbUser.getIdToken() : null;
      const res = await getNotificationDetails(subUserId, notification.id, idToken);
      const d = res?.data?.data || res?.data || {};
      const projectData = {
        id: d.project_id || d.projectId || d.id,
        title: d.title || d.project_title || '',
        description: d.description || d.body || '',
        amount: d.bid_amount || d.amount || 0,
        currency: d.currency || 'USD',
        currencySign: d.currency_sign || d.currencySign || '$',
        seo_url: d.seo_url || d.seoUrl || null,
        _raw: d,
      };
      openModal('projectDetails', projectData);
    } catch (err) {
      console.warn('Failed to load project details', err);
      openModal('projectDetails', notification.projectData || { id: null, title: 'Details unavailable', description: err?.message || String(err) });
    } finally {
      setPendingViewIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleMarkAllRead = async () => {
    const subUserId = selectedSubUser?.document_id || selectedSubUser?.sub_user_id || selectedSubUser?.id;
    setMarkAllReadPending(true);
    try {
      if (subUserId) {
        const idToken = typeof fbUser?.getIdToken === 'function' ? await fbUser.getIdToken() : null;
        const res = await markAllNotificationsRead(subUserId, idToken);
        if (res && res.status >= 200 && res.status < 300) {
          markAllRead();
          setSelectAll(true);
        } else {
          console.warn('mark-all-read failed', res?.status, res?.data);
          markAllRead();
          setSelectAll(true);
        }
      } else {
        markAllRead();
        setSelectAll(true);
      }
    } catch (err) {
      console.warn('Failed to mark all as read', err);
      markAllRead();
      setSelectAll(true);
    } finally {
      setMarkAllReadPending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard/more-settings')}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img src="/Icons/arrow-back.svg" alt="Back" className="w-8 h-8" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          {unreadCount > 0 && (
            <span className="px-3 py-1 bg-green-500 text-white text-sm font-semibold rounded-full">
              {unreadCount} unread
            </span>
          )}
        </div>

        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            disabled={markAllReadPending}
            className="flex items-center gap-3 px-6 py-3 bg-white border-[1.5px] border-primary-blue rounded-xl hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className={`w-6 h-6 rounded border-[1.5px] flex items-center justify-center transition-colors ${selectAll || unreadCount === 0
                ? 'bg-primary-blue border-primary-blue'
                : 'bg-white border-primary-blue'
              }`}>
              {(selectAll || unreadCount === 0) && (
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-base font-normal text-gray-900">
              {markAllReadPending ? 'Marking…' : 'Mark all as read'}
            </span>
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div>
        {grouped.length === 0 ? (
          <div className="p-12 text-center">
            <img src="/Icons/bell-icon.svg" alt="No notifications" className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-gray-500 text-lg">No notifications yet</p>
            <p className="text-gray-400 text-sm mt-2">When you get notifications, they'll show up here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map((n, index) => {
              const isSuccess = typeof n.isSuccess !== 'undefined'
                ? Boolean(n.isSuccess)
                : (n.type === 'success');

              const description = n.description || n.message || (n.data && n.data.description) || '';
              const title = n.title || (typeof n.isSuccess !== 'undefined' ? (isSuccess ? 'Bid Success' : 'Bid Failed') : (n.type === 'success' ? 'Success' : (n.type === 'error' ? 'Error' : 'Info')));

              const accentColor = (typeof n.isSuccess !== 'undefined')
                ? (isSuccess ? '#28a745' : '#dc3545')
                : (n.type === 'success' ? '#28a745' : n.type === 'error' ? '#dc3545' : '#6c757d');

              const isErrorCard = (typeof n.isSuccess !== 'undefined' && n.isSuccess === false) || n.type === 'error';
              const cardBackground = n.read ? 'bg-white' : (isErrorCard ? 'bg-white' : 'bg-white');
              const titleColor = isErrorCard ? 'text-red-700' : 'text-gray-900';
              const descriptionColor = isErrorCard ? 'text-red-600' : 'text-gray-700';

              return (
                <div
                  key={n.id}
                  className={`flex gap-6 p-4 border-[1.5px] rounded-lg ${cardBackground} ${isErrorCard
                      ? 'border-red-500'
                      : (!n.read ? 'border-primary-blue' : 'border-light-blue')
                    }`}
                >
                  <div
                    className="w-3 h-3 mt-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: accentColor }}
                  />

                  <div className="flex-1">
                    <div className="flex items-start mb-2 gap-5">
                      <span className={`font-semibold text-lg ${titleColor}`}>{title}</span>
                      <span className="text-sm text-gray-500 mt-1">{formatTime(n.createdAt)}</span>
                    </div>
                    <div className='flex justify-between gap-2 w-full'>
                      <div className={`text-base ${descriptionColor} mb-4 w-5/6`}>
                        {description || n.message || 'No additional details'}
                      </div>

                      <div className="flex gap-2 h-10 -mt-2 ">
                        {n.projectData && (
                          <button
                            onClick={() => handleView(n)}
                            disabled={pendingViewIds.has(n.id)}
                            className="px-5 h-10 border-[1.5px] border-primary-blue text-primary-blue rounded-lg text-sm font-medium hover:bg-primary-blue hover:text-white transition-colors disabled:opacity-50"
                          >
                            {pendingViewIds.has(n.id) ? 'Loading…' : 'View'}
                          </button>
                        )}

                        <button
                          onClick={() => remove(n.id)}
                          className="flex-shrink-0"
                        >
                          <img
                            src="/Icons/delete-icon.svg"
                            alt="Delete"
                            className="w-10 h-10"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-primary-blue">
          {(() => {
            const page = Number(pagination?.page || 1);
            const limit = Number(pagination?.limit || 10);
            const start = items.length === 0 ? 0 : ((page - 1) * limit) + 1;
            const end = items.length === 0 ? 0 : ((page - 1) * limit) + items.length;
            if (typeof pagination?.total === 'number') return `Showing ${start} - ${end} of ${pagination.total}`;
            return `Showing ${start} - ${end}${hasMore ? ' (more available)' : ''}`;
          })()}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => goToPage(Math.max(1, (pagination?.page || 1) - 1))}
            disabled={!pagination || (Number(pagination.page || 1) <= 1)}
            className={`px-3 py-1 rounded ${Number(pagination?.page || 1) > 1 ? 'bg-white border-2 border-primary-blue text-primary-blue' : 'bg-gray-100 text-primary-blue'}`}
          >
            Prev
          </button>

          <span className="text-primary-blue rounded border-2 border-primary-blue px-3 py-1">Page {pagination?.page || 1}</span>

          <button
            onClick={() => goToPage((Number(pagination?.page || 1) + 1))}
            disabled={!pagination || (typeof pagination.is_next !== 'undefined' ? !pagination.is_next : (pagination.totalPages ? Number(pagination.page) >= Number(pagination.totalPages) : !hasMore))}
            className={`px-3 py-1 rounded ${((pagination && (pagination.is_next === true || (pagination.totalPages && Number(pagination.page) < Number(pagination.totalPages)))) ? 'bg-white border-2 border-primary-blue text-primary-blue' : 'bg-gray-100 text-primary-blue')}`}
          >
            Next
          </button>

          <select
            value={pagination?.limit || 10}
         onChange={(e) => {
              const newLimit = Number(e.target.value) || 10;
              // reload page 1 with new limit
              loadPage && loadPage(1, newLimit, { mode: 'replace', playSound: false });
            }}
            className="border-2 border-primary-blue px-2 py-1 rounded text-primary-blue"
          >
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>

          {/* <button
            onClick={() => loadMore && loadMore()}
            disabled={!hasMore}
            className="px-3 py-1 rounded bg-white border-2 border-primary-blue text-primary-blue"
          >
            Load more
          </button> */}
        </div>
      </div>

      {modalState.isOpen && modalState.type === 'projectDetails' && (
        <ProjectDetailsModal
          projectData={modalState.data}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default Notifications;